/** @jsxImportSource react */
import React, { useCallback, useState } from 'react'
import type { Location } from 'react-router-dom'
import { PAYMENTS_ENABLED } from '@/config/features'
import './AdminUnifiedSidebarNav.css'

/** Sobrevive al remonte al cambiar de layout admin ↔ reclutador. */
const persistedPanelOpen = { admin: false, recruiter: false }

export function resetAdminSidebarPanels(): void {
  persistedPanelOpen.admin = false
  persistedPanelOpen.recruiter = false
}

const shieldIconSvg = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="Escudo"
  >
    <path
      d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
)

const briefcaseIconSvg = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="Maletín"
  >
    <rect
      x="3"
      y="7"
      width="18"
      height="13"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
    />
    <path
      d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
    />
    <path d="M3 12h18" stroke="currentColor" strokeWidth="1.5" />
  </svg>
)

const chevronIconSvg = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    width={16}
    height={16}
    aria-hidden
  >
    <path
      d="M9 18L15 12L9 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const dashboardIconSvg = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <rect x="14" y="14" width="7" height="7" stroke="currentColor" strokeWidth="1.5" fill="none" />
  </svg>
)

const usersIconSvg = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <path
      d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
)

const auditIconSvg = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
    />
    <path
      d="M14 2v6h6M16 13H8M16 17H8M10 9H8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
)

const aiMonitoringIconSvg = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 2a4 4 0 0 1 4 4v2h2a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2V6a4 4 0 0 1 4-4z"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
    />
    <path
      d="M9 14l2 2 4-4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const jdUploadIconSvg = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
    />
    <path d="M14 2V8H20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M8 13H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M8 17H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

const premiumIconSvg = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
    />
  </svg>
)

const settingsIconSvg = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" fill="none" />
    <path
      d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
    />
  </svg>
)

export interface AdminUnifiedSidebarNavProps {
  location: Location
  isSidebarCollapsed: boolean
  onNavigate: (path: string) => void
  t: (key: string) => string
  isPremium?: boolean
  pulsingPath?: string | null
}

type NavLinkDef = { path: string; labelKey: string; icon: React.ReactNode; exact?: boolean }

const ADMIN_LINKS: NavLinkDef[] = [
  { path: '/admin', labelKey: 'admin.dashboard', icon: dashboardIconSvg, exact: true },
  { path: '/admin/users', labelKey: 'admin.users', icon: usersIconSvg },
  { path: '/admin/audit', labelKey: 'admin.audit', icon: auditIconSvg },
  { path: '/admin/ai-monitoring', labelKey: 'admin.aiUsage', icon: aiMonitoringIconSvg },
]

function isPathActive(pathname: string, path: string, exact?: boolean): boolean {
  if (exact) return pathname === path || pathname === `${path}/`
  return pathname.startsWith(path)
}

export function AdminUnifiedSidebarNav({
  location,
  isSidebarCollapsed,
  onNavigate,
  t,
  isPremium = false,
  pulsingPath = null,
}: AdminUnifiedSidebarNavProps) {
  const [adminPanelOpen, setAdminPanelOpenState] = useState(persistedPanelOpen.admin)
  const [recruiterPanelOpen, setRecruiterPanelOpenState] = useState(persistedPanelOpen.recruiter)

  const setAdminPanelOpen = (open: boolean) => {
    persistedPanelOpen.admin = open
    setAdminPanelOpenState(open)
  }
  const setRecruiterPanelOpen = (open: boolean) => {
    persistedPanelOpen.recruiter = open
    setRecruiterPanelOpenState(open)
  }

  const renderNavLink = useCallback(
    ({ path, labelKey, icon, exact }: NavLinkDef) => {
      const label = t(labelKey)
      const active = isPathActive(location.pathname, path, exact)
      const isPulsing = path === pulsingPath
      return (
        <button
          key={path}
          type="button"
          className={`recruiter-sidebar-nav-item recruiter-sidebar-nav-item--nested ${active ? 'active' : ''} ${isPulsing ? 'recruiter-sidebar-nav-item--pulse' : ''}`}
          onClick={() => onNavigate(path)}
          title={isSidebarCollapsed ? label : undefined}
        >
          <span className="nav-item-icon nav-item-icon--expanded">{icon}</span>
          {!isSidebarCollapsed && <span className="nav-item-text">{label}</span>}
        </button>
      )
    },
    [isSidebarCollapsed, location.pathname, onNavigate, pulsingPath, t],
  )

  const recruiterLinks: NavLinkDef[] = [
    { path: '/recruiter', labelKey: 'sidebar.panel', icon: dashboardIconSvg, exact: true },
    { path: '/recruiter/new', labelKey: 'sidebar.new', icon: jdUploadIconSvg },
  ]
  if (PAYMENTS_ENABLED && !isPremium) {
    recruiterLinks.push({
      path: '/recruiter/checkout',
      labelKey: 'sidebar.premium',
      icon: premiumIconSvg,
    })
  }

  const settingsActive = location.pathname.startsWith('/recruiter/settings')

  const panelToggle = (
    panelId: 'admin' | 'recruiter',
    open: boolean,
    setOpen: (v: boolean) => void,
    labelKey: string,
    icon: React.ReactNode,
  ) => {
    const label = t(labelKey)
    return (
      <button
        type="button"
        className={`recruiter-sidebar-nav-item admin-unified-sidebar-group-toggle ${open ? 'admin-unified-sidebar-group-toggle--open' : ''}`}
        aria-expanded={open}
        data-testid={`admin-sidebar-panel-${panelId}`}
        onClick={() => setOpen(!open)}
        title={isSidebarCollapsed ? label : undefined}
      >
        <span className="nav-item-icon nav-item-icon--expanded">{icon}</span>
        {!isSidebarCollapsed && (
          <span className="nav-item-text admin-unified-sidebar-group-label">{label}</span>
        )}
        {!isSidebarCollapsed && (
          <span className="admin-unified-sidebar-chevron" aria-hidden>
            {chevronIconSvg}
          </span>
        )}
      </button>
    )
  }

  return (
    <>
      {panelToggle(
        'admin',
        adminPanelOpen,
        setAdminPanelOpen,
        'admin.sidebar.panelAdmin',
        shieldIconSvg,
      )}
      {adminPanelOpen && !isSidebarCollapsed && (
        <div className="admin-unified-sidebar-group-items" data-testid="admin-sidebar-admin-items">
          {ADMIN_LINKS.map(renderNavLink)}
        </div>
      )}

      {panelToggle(
        'recruiter',
        recruiterPanelOpen,
        setRecruiterPanelOpen,
        'admin.sidebar.panelRecruiter',
        briefcaseIconSvg,
      )}
      {recruiterPanelOpen && !isSidebarCollapsed && (
        <div
          className="admin-unified-sidebar-group-items"
          data-testid="admin-sidebar-recruiter-items"
        >
          {recruiterLinks.map(renderNavLink)}
          {isPremium && (
            <div className="recruiter-sidebar-pro-badge admin-unified-sidebar-pro-badge">
              <span className="badge-icon">⭐</span>
              <span className="badge-text">PRO</span>
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        data-testid="admin-sidebar-settings"
        className={`recruiter-sidebar-nav-item ${settingsActive ? 'active' : ''}`}
        onClick={() => onNavigate('/recruiter/settings')}
        title={isSidebarCollapsed ? t('sidebar.settings') : undefined}
      >
        <span className="nav-item-icon nav-item-icon--expanded">{settingsIconSvg}</span>
        {!isSidebarCollapsed && <span className="nav-item-text">{t('sidebar.settings')}</span>}
      </button>
    </>
  )
}
